/**
 * T550 (FR-UI-003) — Profile-Changelog Bell + Dropdown.
 *
 * Mounted next to the user-avatar in the sidebar's user section
 * (apps/web/src/components/layout/AppShell.tsx). Click opens a
 * dropdown listing the most recent 10 changelog entries; entries
 * with a `ui_route` are clickable TanStack-Router links. Opening the
 * dropdown calls `markAsSeen()` so the badge clears on the next
 * render.
 */
import { useEffect, useRef, useState } from 'react';
import { Link } from '@tanstack/react-router';
import { motion, AnimatePresence } from 'framer-motion';
import { useChangelog, type ChangelogEntry } from '../../hooks/useChangelog';
import { formatDateBerlin } from '../../lib/format-date';

const MAX_VISIBLE_ENTRIES = 10;

function typeBadgeClass(type: ChangelogEntry['type']): string {
  switch (type) {
    case 'feature':
      return 'bg-emerald-500/15 text-emerald-300';
    case 'fix':
      return 'bg-sky-500/15 text-sky-300';
    case 'breaking':
      return 'bg-amber-500/15 text-amber-300';
  }
}

function typeBadgeLabel(type: ChangelogEntry['type']): string {
  switch (type) {
    case 'feature':
      return 'Neu';
    case 'fix':
      return 'Fix';
    case 'breaking':
      return 'Wichtig';
  }
}

export function ChangelogBell() {
  const { entries, unseenCount, markAsSeen } = useChangelog();
  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  // Close on outside-click so the dropdown doesn't trap focus once
  // the user navigates elsewhere in the sidebar. T591 — also close on
  // Escape so keyboard users can dismiss without mouse.
  useEffect(() => {
    if (!open) return;
    function onDocClick(event: MouseEvent) {
      if (!containerRef.current) return;
      if (!containerRef.current.contains(event.target as Node)) {
        setOpen(false);
      }
    }
    function onKeyDown(event: KeyboardEvent) {
      if (event.key === 'Escape') setOpen(false);
    }
    document.addEventListener('mousedown', onDocClick);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('mousedown', onDocClick);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [open]);

  const visible = entries.slice(0, MAX_VISIBLE_ENTRIES);

  return (
    <div ref={containerRef} className="relative" data-testid="sidebar-changelog-bell">
      <button
        type="button"
        data-testid="changelog-bell-button"
        aria-label={
          unseenCount > 0
            ? `Änderungen anzeigen (${unseenCount} neu)`
            : 'Änderungen anzeigen'
        }
        onClick={() => {
          setOpen((prev) => {
            const next = !prev;
            if (next) markAsSeen();
            return next;
          });
        }}
        className="relative p-1.5 text-white/40 hover:text-white transition-colors"
        title="Änderungen + Hinweise"
      >
        <svg
          className="w-4 h-4"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
          aria-hidden="true"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={1.5}
            d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9"
          />
        </svg>
        {unseenCount > 0 && (
          <span
            aria-hidden="true"
            className="absolute top-0 right-0 -mt-0.5 -mr-0.5 min-w-[16px] h-[16px] px-1 rounded-full bg-amber-500 text-[10px] leading-[16px] font-medium text-white text-center"
          >
            {unseenCount > 9 ? '9+' : unseenCount}
          </span>
        )}
      </button>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: -4, scale: 0.98 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: -4, scale: 0.98 }}
            transition={{ duration: 0.12 }}
            data-testid="changelog-bell-dropdown"
            className="absolute bottom-full right-0 mb-2 w-80 max-h-[70vh] overflow-y-auto rounded-lg border border-white/10 bg-zinc-900/95 backdrop-blur-md shadow-xl z-50"
          >
            <div className="p-3 border-b border-white/10">
              <p className="text-sm font-medium text-white">Was ist neu?</p>
              <p className="text-xs text-white/50 mt-0.5">
                Die letzten Updates und Hinweise auf UI-Anpassungen.
              </p>
            </div>
            <ul className="divide-y divide-white/[0.06]">
              {visible.length === 0 && (
                <li className="p-4 text-sm text-white/60">
                  Noch keine Einträge.
                </li>
              )}
              {visible.map((entry, idx) => {
                const body = (
                  <div className="flex items-start gap-2 p-3 hover:bg-white/[0.04] transition-colors">
                    <span
                      className={`inline-block px-1.5 py-0.5 rounded text-[10px] font-medium uppercase ${typeBadgeClass(entry.type)}`}
                    >
                      {typeBadgeLabel(entry.type)}
                    </span>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm text-white">{entry.title}</p>
                      <p className="text-xs text-white/60 mt-0.5">
                        {entry.description}
                      </p>
                      <p className="text-[10px] text-white/40 mt-1">
                        {formatDateBerlin(entry.date)}
                      </p>
                    </div>
                  </div>
                );
                return (
                  <li key={`${entry.date}-${idx}`} data-testid={`changelog-entry-${idx}`}>
                    {entry.ui_route ? (
                      <Link
                        to={entry.ui_route}
                        className="block"
                        onClick={() => setOpen(false)}
                      >
                        {body}
                      </Link>
                    ) : (
                      body
                    )}
                  </li>
                );
              })}
            </ul>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
